/**
 * The Locker (docs/game-design.md §11, Milestone 7): cosmetics bought with coins earned in
 * fights. Looks only: nothing here changes a fighter's stats. Prices come from the Locker
 * design, sized for the coins per fight (O3).
 */

export type CosmeticKind = 'fist' | 'color' | 'effect' | 'pet';

export const COSMETIC_KINDS: readonly CosmeticKind[] = ['fist', 'color', 'effect', 'pet'];

export interface FistItem {
  id: string;
  price: number;
}

export interface ColorItem {
  id: string;
  price: number;
  /**
   * The colour the fighter turns: its own main colour (its rig colour) is turned to this hue, in
   * degrees, so "Crimson" is crimson on every fighter. null keeps the fighter's colours.
   */
  hue: number | null;
  /** Saturation multiplier: 0 is black and white. */
  saturate: number;
}

export interface EffectItem {
  id: string;
  price: number;
  /** What it draws around the fighter. */
  style: 'none' | 'aura' | 'sparks' | 'champion';
  color: string;
  /** Can't be bought until the player has fought in the league's top division (Legend). */
  unlock?: 'legend';
}

/** A little creature that flies beside the fighter (its art and motion: assets/pets/pets.json). */
export interface PetItem {
  id: string;
  price: number;
}

/** A fighter's equipped cosmetics, by item id. */
export interface Look {
  fist: string;
  color: string;
  effect: string;
  pet: string;
}

export const DEFAULT_LOOK: Look = { fist: 'classic', color: 'natural', effect: 'none', pet: 'none' };

export const COSMETICS = {
  /** Glove designs: one image each (assets/gloves), worn by any fighter. Classic: the fighter's own fists. */
  fist: [
    { id: 'classic', price: 0 },
    { id: 'inferno', price: 120 }, // placeholder
    { id: 'frost', price: 150 }, // placeholder
    { id: 'toxic', price: 180 }, // placeholder
    { id: 'shadow', price: 220 }, // placeholder
    { id: 'gold', price: 400 }, // placeholder
  ] satisfies FistItem[],
  color: [
    { id: 'natural', price: 0, hue: null, saturate: 1 },
    { id: 'crimson', price: 100, hue: 350, saturate: 1.1 }, // placeholder
    { id: 'ocean', price: 100, hue: 205, saturate: 1 }, // placeholder
    { id: 'violet', price: 140, hue: 275, saturate: 1 }, // placeholder
    { id: 'lime', price: 140, hue: 95, saturate: 1.1 }, // placeholder
    { id: 'mono', price: 200, hue: null, saturate: 0 }, // placeholder
  ] satisfies ColorItem[],
  effect: [
    { id: 'none', price: 0, style: 'none', color: '#000000' },
    { id: 'glow', price: 160, style: 'aura', color: '#f5a03c' }, // placeholder
    { id: 'sparks', price: 240, style: 'sparks', color: '#f5a03c' }, // placeholder
    { id: 'venom', price: 240, style: 'aura', color: '#7fd66a' }, // placeholder
    { id: 'ice', price: 260, style: 'aura', color: '#7ec8ff' }, // placeholder
    { id: 'champion', price: 500, style: 'champion', color: '#ffd76a', unlock: 'legend' }, // placeholder
  ] satisfies EffectItem[],
  /** Pets fly beside the fighter and sit on its rear glove when it stands still. */
  pet: [
    { id: 'none', price: 0 },
    { id: 'bat', price: 150 }, // placeholder
    { id: 'ghost', price: 180 }, // placeholder
    { id: 'firefly', price: 200 }, // placeholder
    { id: 'owl', price: 220 }, // placeholder
    { id: 'drone', price: 260 }, // placeholder
    { id: 'dragon', price: 350 }, // placeholder
  ] satisfies PetItem[],
};

/** A fighter bought with coins (D52). Looks only, like the rest of the Locker: it fights like any other. */
export interface FighterItem {
  id: string;
  price: number;
}

/** The fighter new players have, and the one a fighter they don't own falls back to. */
export const DEFAULT_FIGHTER = 'spike';

/** The paid fighters, cheapest first. Every fighter not listed here is free. */
export const PAID_FIGHTERS: readonly FighterItem[] = [
  { id: 'ember', price: 750 }, // placeholder
  { id: 'yeti', price: 1000 }, // placeholder
  { id: 'ronin', price: 1250 }, // placeholder
];

/** A fighter's price in coins: 0 for the free ones. */
export function fighterPrice(id: string): number {
  return PAID_FIGHTERS.find((f) => f.id === id)?.price ?? 0;
}

export function cosmeticItem(kind: 'fist', id: string): FistItem | undefined;
export function cosmeticItem(kind: 'color', id: string): ColorItem | undefined;
export function cosmeticItem(kind: 'effect', id: string): EffectItem | undefined;
export function cosmeticItem(kind: 'pet', id: string): PetItem | undefined;
export function cosmeticItem(kind: CosmeticKind, id: string): FistItem | ColorItem | EffectItem | PetItem | undefined;
export function cosmeticItem(kind: CosmeticKind, id: string) {
  return (COSMETICS[kind] as readonly { id: string }[]).find((i) => i.id === id);
}
